import { useEffect } from 'react';
import { httpGet } from '../components/utils';

const isAppcuesScriptReachable = async () => {
    try {
        const response = await fetch('https://notifications.silverfort.com/211104.js', { method: 'HEAD' });
        return response.ok;
    } catch (error) {
        console.error('Error checking feature tour script availability:', error);
        return false;
    }
};

const checkAppcuesEnabled = async () => {
    try {
        const response = await httpGet('/data/featureTourConfig');
        return (response.data?.enabled && !response.data?.offlineMode) || false;
    } catch (error) {
        console.error('Error fetching system data:', error);
        return false;
    }
};

const loadGoogleFonts = () => {
    if (document.querySelector('link[href*="fonts.googleapis.com"]')) return;

    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    preconnect1.crossOrigin = 'anonymous';

    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';

    const poppingsFontLink =
        'https://fonts.googleapis.com/css2?family=Poppins:wght@100;200;300;400;500;600;700;800;900&display=swap';
    const preloadFont = document.createElement('link');
    preloadFont.rel = 'preload';
    preloadFont.as = 'style';
    preloadFont.href = poppingsFontLink;
    preloadFont.crossOrigin = 'anonymous';

    const fontStylesheet = document.createElement('link');
    fontStylesheet.rel = 'stylesheet';
    fontStylesheet.href = poppingsFontLink;
    fontStylesheet.crossOrigin = 'anonymous';
    fontStylesheet.onload = () => {
        fontStylesheet.rel = 'stylesheet';
    };

    document.head.appendChild(preconnect1);
    document.head.appendChild(preconnect2);
    document.head.appendChild(preloadFont);
    document.head.appendChild(fontStylesheet);
};

const isPageWithIframes = () => {
    const pathname = window.location.pathname;
    const iframeRoutes = [
        '/Inventory',
        '/Risk',
        '/AccessAnalysisCloud',
        '/IspmCloud',
        '/ItdrCloud',
        '/CloudNHI',
        '/AIAgentSecurityCloud',
        '/Settings/Cloud%20Integrations'
    ];

    return iframeRoutes.some((route) => pathname.includes(route)) || document.querySelector('iframe') !== null;
};

const setAppcuesIdentifiers = async () => {
    try {
        const appcuesData = await httpGet('/Data/featureTourData');
        if (window.Appcues && appcuesData && appcuesData.data) {
            const { userId, environmentId } = appcuesData.data;
            const uniqueUserId = userId === 'consoleuser' ? `${userId}-${environmentId}` : userId;
            window.Appcues.identify(uniqueUserId, {
                environmentId: appcuesData.data.environmentId,
                customerId: appcuesData.data.customerId,
                userEmail: appcuesData.data.userEmail,
                userDomainName: appcuesData.data.userDomainName,
                companyName: appcuesData.data.companyName,
                version: appcuesData.data.version,
                pasState: appcuesData.data.pasState,
                cloudResourcesState: appcuesData.data.cloudResourcesState,
                envMode: appcuesData.data.envMode,
                cveRebootFixed: appcuesData.data.cveRebootFixed,
                ...appcuesData.data.pricingModulesAppcuesStatus
            });
            console.log('Feature tour user identified.');
            window.Appcues.group(appcuesData.data.environmentId, {
                environmentId: appcuesData.data.environmentId
            });
            console.log('Feature tour group identified.');
        } else {
            console.warn('Feature tour is not available or no data returned.');
        }
    } catch (error) {
        console.error('Error fetching feature tour data:', error);
    }
};

const loadAppcuesScript = () => {
    window.AppcuesSettings = { enableURLDetection: true };

    const script = document.createElement('script');
    script.src = new URL('./bootstrap.js', import.meta.url).href;
    script.async = true;

    script.onload = () => {
        console.log('Feature tour script loaded successfully.');
        loadGoogleFonts();

        if (isPageWithIframes()) {
            console.log('Feature tour: Adding delay for iframe page to allow URL params to load.');
            setTimeout(() => {
                setAppcuesIdentifiers();
            }, 500);
        } else {
            setAppcuesIdentifiers();
        }
    };

    script.onerror = () => {
        console.error('Failed to load feature tour script. Feature tour will not be initialized.');
    };

    document.body.appendChild(script);
};

function useAppcuesScript() {
    useEffect(() => {
        const initializeAppcues = async () => {
            const appcuesEnabled = await checkAppcuesEnabled();
            const appcuesScriptReachable = await isAppcuesScriptReachable();

            if (!appcuesEnabled) {
                console.warn('Feature tour initialization skipped. Feature tour is not enabled.');
            } else if (!appcuesScriptReachable) {
                console.warn('Feature tour initialization skipped. Feature tour is not reachable.');
            } else {
                loadAppcuesScript();
            }
        };

        initializeAppcues();

        return () => {
            const existingScript = document.querySelector('script[src$="bootstrap.js"]');
            if (existingScript) {
                document.body.removeChild(existingScript);
            }

            const existingFonts = document.querySelectorAll(
                'link[href*="fonts.googleapis.com"], link[href*="fonts.gstatic.com"]'
            );
            existingFonts.forEach((fontLink) => fontLink.remove());
        };
    }, []);
}

export default useAppcuesScript;
